import apiClient from './apiClient';

/**
 * Public shape returned by the backend — never contains the encrypted embedding.
 */
export interface Voiceprint {
  id: string;
  userId: string;
  role: 'doctor' | 'nurse' | 'patient';
  embeddingDim: number;
  engine: '3d-speaker';
  engineModel: string | null;
  sampleDurationSec: number;
  qualityScore: number | null;
  isActive: boolean;
  enrolledAt: string;
  revokedAt: string | null;
  consentId: string | null;
}

/**
 * Session-start payload — includes the decrypted embedding so the client can
 * forward it into a HEIDI scribe session. Only returned for the authenticated
 * owner, only for an active voiceprint.
 */
export interface ActiveVoiceprintForSession {
  voiceprintId: string;
  role: 'doctor' | 'nurse' | 'patient';
  engine: '3d-speaker';
  engineModel: string | null;
  embeddingDim: number;
  /** Raw little-endian float32 bytes, L2-normalised, base64. */
  embeddingB64: string;
}

export interface EnrollVoiceprintPayload {
  /** Base64-encoded WAV audio. */
  audio: string;
  role?: 'doctor' | 'nurse' | 'patient';
  sampleDurationSec?: number;
  consentAccepted: boolean;
  replaceActive?: boolean;
}

export const voiceprintApi = {
  listMine: async (): Promise<Voiceprint[]> => {
    const res = await apiClient.get<Voiceprint[] | { data: Voiceprint[] }>(
      '/voiceprints/mine',
    );
    const body = res.data as Voiceprint[] | { data?: Voiceprint[] };
    if (Array.isArray(body)) return body;
    return Array.isArray(body?.data) ? body.data : [];
  },

  enroll: async (payload: EnrollVoiceprintPayload): Promise<Voiceprint> => {
    const res = await apiClient.post<Voiceprint>('/voiceprints/enroll', payload);
    return res.data;
  },

  revoke: async (id: string): Promise<Voiceprint> => {
    const res = await apiClient.delete<Voiceprint>(`/voiceprints/${id}`);
    return res.data;
  },

  getActiveForSession: async (
    role: 'doctor' | 'nurse' | 'patient' = 'doctor',
  ): Promise<ActiveVoiceprintForSession | null> => {
    try {
      const res = await apiClient.get<ActiveVoiceprintForSession>(
        '/voiceprints/mine/active',
        { params: { role } },
      );
      return res.data;
    } catch (err: unknown) {
      // 404 means "no active voiceprint for this role" — not an error condition.
      const e = err as { response?: { status?: number } };
      if (e.response?.status === 404) return null;
      throw err;
    }
  },
};
